import { useEffect, useRef, useState, type FormEvent } from 'react';
import { trackPageView, Analytics } from '../utils/analytics';
import { sendContactForm, type ContactFormData } from '../api/contact';
import '../styles/audit.css';
import '../styles/reports.css';

/* ============================================
   INLINE SVG ICONS
   ============================================ */

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconArrow = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5l7 7-7 7" />
  </svg>
);

const IconShield = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

/* ============================================
   REFS CAROUSEL — auto-discovery (copied from audit)
   ============================================ */

const logoModules = import.meta.glob('../assets/audit/logos/client-*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const logos: string[] = Object.entries(logoModules)
  .map(([path, url]) => {
    const m = path.match(/client-(\d+)\.png$/);
    return { n: m ? parseInt(m[1], 10) : 0, url };
  })
  .sort((a, b) => a.n - b.n)
  .map(x => x.url);

const MARQUEE_SPEED = 40;

function RefsCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isHovered = useRef(false);
  const isDragRef = useRef(false);
  const dragState = useRef<{ startX: number; scrollLeft: number; moved: boolean } | null>(null);

  const COPIES = 3;
  const displayLogos = Array.from({ length: COPIES }, () => logos).flat();

  useEffect(() => {
    const el = trackRef.current;
    if (!el || logos.length === 0) return;

    const initScroll = () => {
      const oneCopyWidth = el.scrollWidth / COPIES;
      el.scrollLeft = oneCopyWidth;
    };
    initScroll();
    const onResize = () => initScroll();
    window.addEventListener('resize', onResize);

    let rafId = 0;
    let lastTime = performance.now();

    const loop = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!isHovered.current && !isDragRef.current) {
        el.scrollLeft += MARQUEE_SPEED * dt;
      }

      const oneCopyWidth = el.scrollWidth / COPIES;
      if (el.scrollLeft >= oneCopyWidth * 2) {
        el.scrollLeft -= oneCopyWidth;
      } else if (el.scrollLeft < oneCopyWidth * 0.5 && dragState.current === null) {
        el.scrollLeft += oneCopyWidth;
      }

      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { startX: e.clientX, scrollLeft: el.scrollLeft, moved: false };
    isDragRef.current = true;
    setIsDragging(true);
    el.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el || !dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 4) dragState.current.moved = true;
    el.scrollLeft = dragState.current.scrollLeft - dx;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (el) el.releasePointerCapture?.(e.pointerId);
    isDragRef.current = false;
    setIsDragging(false);
    setTimeout(() => { dragState.current = null; }, 50);
  };

  if (logos.length === 0) return null;

  return (
    <div className="audit-refs-carousel">
      <div
        ref={trackRef}
        className={`audit-refs-track${isDragging ? ' audit-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={() => { isHovered.current = true; }}
        onMouseLeave={() => { isHovered.current = false; }}
      >
        {displayLogos.map((url, i) => (
          <div key={i} className="audit-ref-tile">
            <img src={url} alt="Reference klienta" loading="lazy" draggable={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================
   NAV
   ============================================ */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav className={`audit-nav${scrolled ? ' scrolled' : ''}`}>
      <div className="audit-nav-inner">
        <a href="/" className="audit-nav-logo">
          <img src="/logo_light2.png" alt="SyncroX" />
        </a>
        <a href="#form" className="audit-nav-cta">Chci manažerské reporty</a>
      </div>
    </nav>
  );
}

/* ============================================
   MAIN CONTACT FORM
   ============================================ */

function ReportForm() {
  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: 'manazerske-reporty', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Mám zájem o manažerské reporty.',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Hotovo. Ozveme se do 24 hodin.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: 'manazerske-reporty', message: '' });
        Analytics.contactFormSubmit('manazerske-reporty');
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Chyba připojení. Napište nám na obchod@syncrox.cz.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="audit-form" onSubmit={handleSubmit}>
      <h3>Chci vidět firmu z mobilu</h3>
      <div className="audit-form-sub">Zdarma 30min konzultace · bez závazku</div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>Jméno *</label>
          <input className="audit-form-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jan Novák" />
        </div>
        <div className="audit-form-group">
          <label>Firma *</label>
          <input className="audit-form-input" required value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Firma s.r.o." />
        </div>
      </div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>E-mail *</label>
          <input className="audit-form-input" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jan@firma.cz" />
        </div>
        <div className="audit-form-group">
          <label>Telefon</label>
          <input className="audit-form-input" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+420..." />
        </div>
      </div>
      <div className="audit-form-group">
        <label>Jaké systémy používáte? (nepovinné)</label>
        <textarea className="audit-form-textarea" rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Např. Pohoda + Shoptet + GoPay..." />
      </div>
      <button type="submit" className="audit-form-submit" disabled={submitting}>
        {submitting ? 'Odesílám…' : 'Domluvit konzultaci zdarma'}
      </button>
      {status && <div className={`audit-form-status ${status.type}`}>{status.msg}</div>}
      <p className="audit-form-fine">Ozveme se do 24 hodin. Obvykle dřív.</p>
    </form>
  );
}

/* ============================================
   EXIT INTENT POPUP
   ============================================ */

function ExitIntent() {
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);

  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: 'exit-audit-zdarma', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    // Already shown in this browser session?
    if (sessionStorage.getItem('rep-exit-shown') === '1') {
      shownRef.current = true;
      return;
    }

    const onMouseLeave = (e: MouseEvent) => {
      if (shownRef.current) return;
      // Trigger only if leaving through top edge (user heading to tab/address bar)
      if (e.clientY <= 0) {
        shownRef.current = true;
        sessionStorage.setItem('rep-exit-shown', '1');
        setOpen(true);
      }
    };

    // Delay arm to avoid false triggers on load
    const timer = setTimeout(() => {
      document.documentElement.addEventListener('mouseleave', onMouseLeave);
    }, 3000);

    // Backup trigger: after 60s of time on page
    const backupTimer = setTimeout(() => {
      if (!shownRef.current) {
        shownRef.current = true;
        sessionStorage.setItem('rep-exit-shown', '1');
        setOpen(true);
      }
    }, 60_000);

    return () => {
      clearTimeout(timer);
      clearTimeout(backupTimer);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Chci audit zdarma (exit popup).',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Hotovo! Ozveme se do 24 hodin.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: 'exit-audit-zdarma', message: '' });
        Analytics.contactFormSubmit('exit-audit-zdarma');
        setTimeout(() => setOpen(false), 1800);
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Napište nám na obchod@syncrox.cz.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`rep-exit-overlay${open ? ' rep-exit-open' : ''}`} onClick={() => setOpen(false)}>
      <div className="rep-exit-card" onClick={e => e.stopPropagation()}>
        <button type="button" className="rep-exit-close" onClick={() => setOpen(false)} aria-label="Zavřít">×</button>
        <div className="rep-exit-top">
          <div className="rep-exit-label">Počkejte chvilku</div>
          <h3>Než odejdete — chcete zdarma audit vaší firmy?</h3>
          <p>60 minut s expertem · PDF plán s konkrétními čísly · mobilní aplikace zdarma</p>
        </div>
        <div className="rep-exit-body">
          <div className="rep-exit-perks">
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>Analýza vašich systémů</strong> — co se dá zdigitalizovat u vás konkrétně</div>
            </div>
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>Vyčíslené úspory v Kč</strong> — ne průměry, vaše čísla</div>
            </div>
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>PDF plán zůstává váš</strong> — i když dál nepokračujete</div>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="rep-exit-form-row">
              <input className="rep-exit-input" required placeholder="Jméno" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <input className="rep-exit-input" required placeholder="Firma" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />
            </div>
            <input className="rep-exit-input" required type="email" placeholder="E-mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <button type="submit" className="rep-exit-submit" disabled={submitting}>
              {submitting ? 'Odesílám…' : 'Chci audit zdarma'}
            </button>
            {status && <div className={`rep-exit-status ${status.type}`}>{status.msg}</div>}
            <p className="rep-exit-fine">Bez spamu. Bez prodejních tlaků. Ozveme se jednou — do 24 hodin.</p>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================================
   FAQ
   ============================================ */

function Faq() {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: 'Kolik to stojí?', a: 'Začíná to na 1 299 Kč měsíčně (manažerské reporty + mobilní aplikace + napojení banky a platební brány). Fakturační a rezervační systémy se počítají od 4 Kč za doklad — čím víc dokladů, tím nižší sazba. Většina firem vyjde na 2 000–4 000 Kč měsíčně. Nasazení, setup i zaškolení máte při podpisu zdarma. Přesné číslo spočítáme na 30minutové konzultaci před vámi.' },
    { q: 'Funguje to s naším účetním systémem?', a: 'Pohoda, Money S3, Helios, Flexi, Abra — všechno běžné v ČR umíme. Pokud máte něco jiného, ozvěte se a zjistíme. Doposud jsme každý systém nakonec propojili.' },
    { q: 'Co když máme specifické procesy?', a: 'Každá firma je jiná — reporty vám nastavujeme přímo na míru vašim procesům. Žádná šablona, ve které se musíte mačkat. Setup je součástí ceny.' },
    { q: 'Je to zabezpečené?', a: 'Data běží v našem cloudu v EU, zálohujeme denně, komunikace šifrovaná. Přístup k reportům má jen ten, kdo má účet — ne celá firma. GDPR-compliant.' },
    { q: 'Jak dlouho trvá rozjezd?', a: 'Typicky 5–10 pracovních dní od dodání přístupů. Některé jednoduché případy spustíme během 48 hodin. Během rozjezdu vás provedeme krok po kroku.' },
    { q: 'Musíme se k něčemu zavazovat?', a: 'Ne. Jedeme měsíc po měsíci. Pokud vám to nevyhovuje, vypnete. Žádné výpovědní doby, žádné skryté smlouvy.' },
  ];
  return (
    <div className="audit-faq">
      {faqs.map((f, i) => (
        <div key={i} className={`audit-faq-item${open === i ? ' open' : ''}`}>
          <button type="button" className="audit-faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
            <span>{f.q}</span>
            <span className="audit-faq-toggle">+</span>
          </button>
          <div className="audit-faq-a">{f.a}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================
   MAIN PAGE
   ============================================ */

// Integration logos — only the good-looking ones
const integrationLogos = [
  { name: 'Pohoda', src: '/integrations/pohoda.png' },
  { name: 'Money S3', src: '/integrations/moneyS3.webp' },
  { name: 'Abra Flexibee', src: '/integrations/abra flexibee.jpg' },
  { name: 'Shoptet', src: '/integrations/eshop-rychle.png' },
  { name: 'GoPay', src: '/integrations/pays.png' },
  { name: 'Comgate', src: '/integrations/comgate.jpg' },
  { name: 'PayPal', src: '/integrations/paypal.png' },
  { name: 'Mews', src: '/integrations/mews.webp' },
  { name: 'Previo', src: '/integrations/previo.png' },
  { name: 'Reenio', src: '/integrations/reenio.png' },
  { name: 'Zásilkovna', src: '/integrations/Zasilkovna.png' },
  { name: 'PPL', src: '/integrations/ppl.png' },
];

export default function ReportsLanding() {
  useEffect(() => {
    trackPageView('/manazerske-reporty', 'SyncroX — Manažerské reporty a mobilní aplikace pro vaši firmu');
    window.scrollTo(0, 0);

    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const a = t.closest('a');
      if (!a || !a.hash || a.origin !== window.location.origin) return;
      const el = document.querySelector(a.hash);
      if (!el) return;
      e.preventDefault();
      const y = (el as HTMLElement).getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="audit-page reports-page">
      <Nav />

      {/* ========== HERO ========== */}
      <section className="audit-hero">
        <div className="audit-container">
          <div className="audit-hero-grid">
            <div>
              <div className="rep-hero-trustrow">
                <span className="rep-trust-stars">★★★★★</span>
                <span>4,9 / 5</span>
                <span style={{ color: 'var(--a-muted)' }}>·</span>
                <span>50+ firem</span>
              </div>
              <div className="audit-hero-eyebrow">
                Vše v jedné ceně · mobilní aplikace zdarma
              </div>
              <h1>
                Vaše firma <span className="audit-underline">na mobilu</span>. Každý den. Každé číslo, které potřebujete.
              </h1>
              <p className="audit-hero-sub">
                Propojíme vaše systémy, nastavíme reporty přímo z vašeho účetnictví a dáme vám <strong>mobilní aplikaci, kde uvidíte firmu v reálném čase</strong> — z kavárny, z auta, i o víkendu.
              </p>
              <div className="audit-hero-ctas">
                <a href="#form" className="audit-btn-primary">
                  Chci vidět firmu z mobilu
                  <IconArrow />
                </a>
                <a href="#features" className="audit-btn-secondary">Co aplikace ukazuje</a>
              </div>
              <div className="audit-hero-trust">
                <div className="audit-trust-item">
                  <div className="audit-trust-n">Realtime</div>
                  <div className="audit-trust-l">Aktuální data<br/>každou hodinu</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">0 Kč</div>
                  <div className="audit-trust-l">Mobilní aplikace<br/>iOS + Android</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">Vše v ceně</div>
                  <div className="audit-trust-l">Integrace + účetnictví<br/>bez příplatků</div>
                </div>
              </div>
            </div>

            {/* Hero visual — Phone with app */}
            <div className="rep-hero-visual">
              <div className="rep-phone-frame">
                <div className="rep-phone-notch" />
                <div className="rep-phone-screen">
                  <img src="/dash_mobil.png" alt="Manažerský přehled v mobilní aplikaci" />
                </div>
              </div>
              <div className="rep-phone-badge rep-phone-badge-1">
                <div className="rep-phone-badge-dot" />
                <div>Obrat dneška<br/><span style={{ color: 'var(--a-primary)' }}>87&nbsp;340&nbsp;Kč</span></div>
              </div>
              <div className="rep-phone-badge rep-phone-badge-2">
                <div className="rep-phone-badge-dot rep-accent" />
                <div>Nový alert<br/><span style={{ color: 'var(--a-accent)' }}>Pozor: propad</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BIG NUMBER BAND ========== */}
      <section className="audit-bignum">
        <div className="audit-container">
          <div className="audit-bignum-num">30 sekund</div>
          <div className="audit-bignum-label">
            <strong>Za 30 vteřin víte, jak na tom vaše firma je</strong> — místo 30 dní čekání<br/>
            na uzávěrku od účetní.
          </div>
        </div>
      </section>

      {/* ========== CLIENT LOGOS — MARQUEE ========== */}
      <section className="audit-refs">
        <div className="audit-container">
          <div className="audit-refs-head">
            <div className="audit-refs-title">Už používá</div>
            <div className="audit-refs-sub">Klienti, kteří řídí firmu z mobilu</div>
          </div>
          <RefsCarousel />
          <div className="audit-refs-note">
            E-shopy, výroba, hotelnictví i služby. <strong>Všichni přestali čekat na účetní výkazy.</strong>
          </div>
        </div>
      </section>

      {/* ========== PROBLEM ========== */}
      <section className="audit-section" id="problem">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Poznáte se?</span>
            <h2>Řídíte firmu naslepo, protože<br/>nemáte data v reálném čase</h2>
            <p>Pokud kývete u 3 a víc bodů, manažerské reporty jsou přesně pro vás.</p>
          </div>
          <div className="audit-pains">
            {[
              'Na manažerský výkaz čekáte, až ho účetní pošle po uzávěrce — to už je ale na rozhodnutí pozdě.',
              'Když vám někdo položí otázku „jak se nám daří?", nemáte rychlou odpověď. Říkáte „nejspíš dobře".',
              'Nemáte ucelený přehled o tom, kolik reálně máte peněz (banka, peníze na cestě, nezaplacené faktury).',
              'Tušíte, co se prodává, ale nevíte to přesně. A které produkty vám zabírají sklad a neprodávají se.',
              'Pohledávky hlídáte „když si vzpomenete" — klient pak dluží 90 dní a vy to zjistíte náhodou.',
              'Vaše data jsou v 4+ systémech, které spolu nemluví. Dát je dohromady znamená celý den v Excelu.',
            ].map((pain, i) => (
              <div key={i} className="audit-pains-item">
                <div className="audit-pains-x">✕</div>
                <div>{pain}</div>
              </div>
            ))}
            <div className="audit-pains-punch">
              Každý den bez přehledu je den, kdy <span className="audit-gold-under">rozhodujete podle pocitu</span>,<br/>
              místo podle čísel.
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL — BIG QUOTE ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="rep-quote-block">
            <div className="rep-quote-text">
              Před rokem jsem manželce odpovídal <strong>„myslím, že dobře"</strong>. Dneska jí řeknu přesně kolik jsme tento týden vydělali, kdo nám dluží, co se prodává a co ne. <strong>Firmu mám v telefonu</strong> místo v hlavě.
            </div>
            <div className="rep-quote-author">
              <div className="rep-quote-avatar">JN</div>
              <div className="rep-quote-meta">
                <div className="rep-quote-name">Jan Novák</div>
                <div className="rep-quote-role">Majitel, e-shop s doplňky stravy</div>
                {/* Canonical: Jan Novák = e-shop s doplňky stravy (JN) */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FEATURES — WHAT YOU SEE IN APP ========== */}
      <section className="audit-section" id="features">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co vidíte v aplikaci</span>
            <h2>5 čísel, která řeknou všechno</h2>
            <p>Ne tabulka s 80 sloupci. Pět klíčových ukazatelů na jedné obrazovce.</p>
          </div>
          <div className="rep-features">
            {[
              {
                n: '01',
                t: 'Obrat dneška',
                d: 'V reálném čase. V 8 ráno víte, kolik přišlo od půlnoci. V 20:00 víte, jak jste skončili den.',
                visual: <>87&nbsp;340&nbsp;Kč<small>Obrat dneška</small></>,
                cls: '',
              },
              {
                n: '02',
                t: 'Reálný cash',
                d: 'Peníze na účtech + na cestě − zítřejší platby − rezerva na daně. Číslo, se kterým pracujete.',
                visual: <>187&nbsp;000&nbsp;Kč<small>Volný cash</small></>,
                cls: 'rep-green',
              },
              {
                n: '03',
                t: 'Pohledávky',
                d: 'Seznam klientů, kteří vám dluží, seřazený od nejstaršího. Víte, komu zvednout telefon.',
                visual: <>84 dní<small>Nejstarší pohledávka</small></>,
                cls: 'rep-red',
              },
              {
                n: '04',
                t: 'Top produkty',
                d: 'Co se prodává. Co leží. Který produkt dělá 40 % obratu — a který zabírá sklad zbytečně.',
                visual: <>TOP 10<small>Produktů podle obratu</small></>,
                cls: '',
              },
              {
                n: '05',
                t: 'Alerty',
                d: 'Propad prodeje, neobvyklá transakce, překročení rozpočtu — systém vám pípne na mobil ráno.',
                visual: <>Alert<small>Propad produktu X</small></>,
                cls: 'rep-accent',
              },
            ].map((f, i) => (
              <div key={i} className="rep-feature">
                <div className="rep-feature-n">{f.n}</div>
                <div className="rep-feature-t">{f.t}</div>
                <div className="rep-feature-d">{f.d}</div>
                <div className={`rep-feature-visual ${f.cls}`}>{f.visual}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== VALUE STACK ========== */}
      <section className="audit-section audit-section-alt" id="stack">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Cena</span>
            <h2>Jedna cena. Všechno v ní.</h2>
            <p>Jeden měsíční tarif podle velikosti vaší firmy. Nasazení, integrace i mobilní aplikaci máte v ceně — žádný setup navíc, žádné příplatky, žádná smlouva na roky.</p>
          </div>
          <div className="audit-stack">
            <div className="audit-stack-header">
              <div>
                <div className="audit-stack-header-l">Co je v tarifu</div>
                <div className="audit-stack-header-t">Manažerské reporty a všechno, co je rozhýbe</div>
              </div>
            </div>
            {[
              {
                t: 'Manažerské reporty na míru',
                d: 'Nastavené přímo na vaše procesy, ne šablona. 5 klíčových čísel + rozpady, které si řeknete. Průběžně je dolaďujeme, jak firma roste.',
              },
              {
                t: 'Napojení účetnictví, banky a platebních bran',
                d: 'Pohoda, Money, Helios, Flexi, Abra + GoPay, Comgate, Stripe, PayPal. Data natékají sama a průběžně — ne ruční import jednou za měsíc.',
              },
              {
                t: 'E-shopy a rezervační systémy',
                d: 'Shoptet, WooCommerce, Shopify, Mews, Previo, Reenio a další. Napojíme, co používáte — účtuje se podle počtu dokladů.',
              },
              {
                t: 'Technická podpora česky',
                d: 'Reagujeme typicky do 4 hodin, během pracovních dní. Když něco nefunguje, řešíme my — ne vy.',
              },
              {
                t: 'Mobilní aplikace iOS + Android',
                d: 'Firma v kapse. Push alerty na propad prodeje, dluh po splatnosti i neobvyklou transakci. Nativní aplikace, ne webová.',
                bonus: true,
                was: '1 500 Kč / měs',
              },
              {
                t: 'Nasazení, konfigurace a zaškolení',
                d: 'Rozjezd do 5–10 dní. Napojíme systémy, nastavíme reporty a zaškolíme celý tým. Jinde jednorázová položka.',
                bonus: true,
                was: '15 000 Kč',
              },
            ].map((it, i) => (
              <div key={i} className={`audit-stack-item${it.bonus ? ' audit-stack-bonus' : ''}`}>
                <div className="audit-stack-l">
                  <div className="audit-stack-check"><IconCheck size={14} /></div>
                  <div>
                    <div className="audit-stack-title">{it.t}</div>
                    <div className="audit-stack-desc">{it.d}</div>
                  </div>
                </div>
                {it.bonus && (
                  <div className="rep-stack-freeprice">
                    <span className="rep-stack-was">{it.was}</span>
                    <span className="rep-stack-free">zdarma</span>
                  </div>
                )}
              </div>
            ))}
            <div className="audit-stack-total">
              <div className="audit-stack-total-row">
                <div className="audit-stack-total-l">Jednorázově zdarma při podpisu</div>
                <div className="audit-stack-total-v audit-strike">přes 30 000 Kč</div>
              </div>
              <div className="audit-stack-total-row">
                <div className="audit-stack-total-l audit-stack-final-l">Měsíční tarif, kdykoliv zrušitelný</div>
                <div className="audit-stack-total-v audit-stack-final-v">
                  od 1&nbsp;299&nbsp;Kč<span className="rep-price-per">/ měsíc</span>
                </div>
              </div>
            </div>

            {/* Transparentní rozpad měsíční ceny */}
            <div className="rep-price-breakdown">
              <div className="rep-price-breakdown-head">Z čeho se měsíční tarif skládá — a přesně víte za co:</div>
              <div className="rep-price-rows">
                <div className="rep-price-row">
                  <div className="rep-price-row-l">
                    <strong>Manažerské reporty + mobilní aplikace</strong>
                    <span>5 klíčových čísel na mobilu, iOS i Android</span>
                  </div>
                  <div className="rep-price-row-v">999&nbsp;Kč<small>/ měs</small></div>
                </div>
                <div className="rep-price-row">
                  <div className="rep-price-row-l">
                    <strong>Banka a platební brány</strong>
                    <span>Transakce automaticky natékají do reportů</span>
                  </div>
                  <div className="rep-price-row-v">+&nbsp;300&nbsp;Kč<small>/ měs</small></div>
                </div>
                <div className="rep-price-row">
                  <div className="rep-price-row-l">
                    <strong>Fakturace a rezervace</strong>
                    <span>Podle počtu dokladů — víc dokladů, nižší sazba</span>
                  </div>
                  <div className="rep-price-row-v">od&nbsp;4&nbsp;Kč<small>/ doklad</small></div>
                </div>
              </div>
              <div className="rep-price-typical">
                Menší firma tak začíná na <strong>1 299 Kč měsíčně</strong>, většina platí <strong>2 000–4 000 Kč</strong> — míň, než stojí pár hodin práce účetní na výkazech.
              </div>
            </div>

            <div className="audit-stack-cta-wrap">
              <a href="#form" className="audit-btn-primary">
                Spočítat přesnou cenu pro naši firmu
                <IconArrow />
              </a>
              <div className="rep-price-note">
                Přesné číslo závisí na počtu systémů a dokladů. Na 30min konzultaci ho <strong>spočítáme rovnou před vámi</strong> — uvidíte, z čeho se skládá každá koruna. Žádné „pošleme nabídku e‑mailem".
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 3 MINI TESTIMONIALS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co říkají klienti</span>
            <h2>Firmy, které přestaly hádat</h2>
          </div>
          <div className="rep-quotes3">
            {[
              {
                stars: 5,
                text: 'Před tím mi účetní poslala výkaz až po uzávěrce a já z něj nic nepoznal. <strong>Teď mám čísla na mobilu každé ráno u kafe.</strong>',
                name: 'Martin Kovář',
                role: 'CEO, výrobní firma',
                avatar: 'MK',
              },
              {
                stars: 5,
                text: 'Nejvíc mě překvapilo, kolik toho zjistím z dat. <strong>Neobsazenost o víkendech mi ukázala,</strong> že musím upravit cenotvorbu — udělala jsem to.',
                name: 'Petra Sýkorová',
                role: 'Jednatelka, rodinný hotel 3*',
                avatar: 'PS',
              },
              {
                stars: 5,
                text: 'Klientům nabízím reporty jako službu navíc. <strong>Místo přepisování děláme poradenství</strong> — a máme lepší marži.',
                name: 'Tomáš Vlk',
                role: 'Jednatel, účetní firma',
                avatar: 'TV',
              },
            ].map((q, i) => (
              <div key={i} className="rep-quote-mini">
                <div className="rep-quote-mini-stars">{'★'.repeat(q.stars)}</div>
                <div className="rep-quote-mini-text" dangerouslySetInnerHTML={{ __html: q.text }} />
                <div className="rep-quote-mini-author">
                  <div className="rep-quote-mini-avatar">{q.avatar}</div>
                  <div className="rep-quote-mini-meta">
                    <div className="rep-quote-mini-name">{q.name}</div>
                    <div className="rep-quote-mini-role">{q.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== INTEGRATIONS ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Napojíme to, co už máte</span>
            <h2>Pohoda, Money, Shoptet a 20+ dalších</h2>
            <p>Integrace jsou součástí tarifu. Žádné příplatky za každý nový systém.</p>
          </div>
          <div className="rep-integrations">
            <div className="rep-int-grid">
              {integrationLogos.map((l, i) => (
                <div key={i} className="rep-int-tile" title={l.name}>
                  <img src={l.src} alt={l.name} loading="lazy" />
                </div>
              ))}
            </div>
            <div className="rep-int-more">
              Nevidíte váš systém? <strong>Doděláme ho.</strong> Každý datový zdroj je nějak připojitelný.
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="audit-section" id="how">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Jak to probíhá</span>
            <h2>3 kroky. Rozjezd do 10 dní.</h2>
          </div>
          <div className="audit-steps">
            <div className="audit-step">
              <div className="audit-step-num">KROK 01</div>
              <div className="audit-step-time">30 minut</div>
              <h3>Konzultace a plán</h3>
              <p>Projdeme vaše systémy a procesy. Řekneme, jaké reporty pro vás dávají smysl, a dostanete <strong>konkrétní cenu</strong>.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 02</div>
              <div className="audit-step-time">5–10 dní</div>
              <h3>Napojení a nastavení</h3>
              <p>Propojíme vaše účetnictví a systémy. Nastavíme reporty přímo na vaše procesy. Vy děláte své.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 03</div>
              <div className="audit-step-time">navždy</div>
              <h3>Firma na dlani</h3>
              <p>Otevřete aplikaci a vidíte vše. Každý den. Průběžně dolaďujeme, když chcete další pohled — bez příplatku.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CASE STUDIES ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Čísla z reality</span>
            <h2>Co manažerské reporty přinesly<br/>konkrétním firmám</h2>
          </div>
          <div className="audit-cases">
            {[
              {
                tag: 'E-shop s doplňky stravy',
                img: '/cases/case-eshop.jpg',
                alt: 'E-shop sklad',
                before: 'Reporty z Excelu, data měsíc pozadu',
                after: 'Obrat dneška na mobilu, alerty na propad',
                result: 'Během 4 měsíců objevili ztrátový produkt a opravili marže',
              },
              {
                tag: 'Rodinný hotel, 3*',
                img: '/cases/case-hotel.jpg',
                alt: 'Rodinný hotel',
                before: 'Obsazenost tušili, cash flow odhadovali',
                after: 'Obsazenost + cash v reálném čase na mobilu',
                result: 'Ceník přizpůsobili podle dat — obrat za sezónu ↑ 18 %',
              },
              {
                tag: 'Výrobní firma, 15 lidí',
                img: '/cases/case-vyroba.jpg',
                alt: 'Výrobní firma',
                before: 'Pohledávky hlídal jednatel "když si vzpomene"',
                after: 'Alerty nad 30 dní po splatnosti',
                result: 'Pohledávky po splatnosti klesly o 62 %',
              },
            ].map((c, i) => (
              <div key={i} className="audit-case">
                <div className="audit-case-img">
                  <img src={c.img} alt={c.alt} loading="lazy" />
                </div>
                <div className="audit-case-head">{c.tag}</div>
                <div className="audit-case-body">
                  <div className="audit-case-row audit-case-row-before">
                    <div className="audit-case-row-label">Dřív</div>
                    <div className="audit-case-row-text">{c.before}</div>
                  </div>
                  <div className="audit-case-row audit-case-row-after">
                    <div className="audit-case-row-label">Dnes</div>
                    <div className="audit-case-row-text">{c.after}</div>
                  </div>
                  <div className="audit-case-result">
                    <div className="audit-case-result-l">Výsledek</div>
                    <div className="audit-case-result-v">{c.result}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== OBJECTIONS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co si možná říkáte</span>
            <h2>A proč to je jinak</h2>
          </div>
          <div className="audit-objections">
            {[
              { q: 'Stačí nám Excel a účetní.', a: 'Fajn. Kolik hodin týdně strávíte tím, že z účetnictví a systémů dáváte data do Excelu? A jak dlouho potom trvá, než reálně <strong>uvidíte odpověď</strong>? Tohle řešení vám to dá za sekundy, pořád.' },
              { q: 'Určitě je to drahé.', a: 'Startuje to na <strong>1 299 Kč měsíčně</strong>, u většiny firem to vyjde na 2–4 tisíce — méně, než kolik stojí 2–3 hodiny práce účetní na výkazech. A to tam máte mobilní aplikaci, nasazení i všechny integrace v ceně.' },
              { q: 'Máme specifické systémy.', a: 'Pravděpodobně už je umíme — Pohoda, Money, Helios, Flexi, Abra a další. Pokud ne, <strong>doděláme</strong>. Zatím jsme ještě žádný systém odmítnout nemuseli.' },
              { q: 'Kdo to u nás bude obsluhovat?', a: 'Nikdo u vás. Reporty i aplikace jsou hotové, jenom se přihlašujete. Úpravy si můžete říct kdykoliv nám — <strong>zahrnuto v tarifu</strong>.' },
              { q: 'Bojíme se o zabezpečení dat.', a: 'Data máme v EU cloudu, šifrované přenosy, denní zálohy, GDPR compliance. Přístup kontroluje admin vaší firmy — ne celá firma vidí všechno.' },
              { q: 'Vyzkouším si to na měsíc?', a: 'Ano. Měsíc po měsíci, bez výpovědní doby. Pokud vám to nevyhovuje, <strong>vypnete</strong>. Žádné smluvní pasti.' },
            ].map((o, i) => (
              <div key={i} className="audit-obj">
                <div className="audit-obj-q">{o.q}</div>
                <div className="audit-obj-a" dangerouslySetInnerHTML={{ __html: o.a }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== REVIEWS STATS ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="rep-reviews">
            <div className="rep-reviews-big">
              <div className="rep-reviews-stars">★★★★★</div>
              <div className="rep-reviews-score">4,9<small>/ 5</small></div>
              <div className="rep-reviews-count">Průměrné hodnocení od 50+ klientů</div>
            </div>
            <div className="rep-reviews-right">
              <h3>96 % klientů doporučuje dál</h3>
              <p>Nejčastější důvod, proč nás klienti doporučují: <strong>konečně vědí, jak na tom jejich firma reálně je</strong> — bez hádání, bez excelů, bez čekání na účetní. A když něco chtějí jinak, stačí říct.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== GUARANTEE ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-guarantee">
            <IconShield size={48} />
            <h3>Férová nabídka — bez háčků</h3>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', margin: '12px auto 20px', maxWidth: 540, lineHeight: 1.55 }}>
              Chceme, aby vám to dávalo smysl. Proto stojí pravidla takhle:
            </p>
            <div className="audit-guarantee-list">
              {[
                '30minutová konzultace je zdarma — bez závazku.',
                'Jeden tarif zahrnuje všechno — žádné skryté položky.',
                'Mobilní aplikace je v ceně. Stáhnete si ji a používáte.',
                'Měsíc po měsíci. Žádné výpovědní doby, žádné sankce.',
                'Úpravy reportů podle vašich potřeb — bez příplatků.',
              ].map((item, i) => (
                <div key={i} className="audit-guarantee-item">
                  <IconCheck size={20} />
                  <div>{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Časté otázky</span>
            <h2>Co se ptají nejčastěji</h2>
          </div>
          <Faq />
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="audit-cta-section" id="form">
        <div className="audit-container">
          <div className="audit-cta-inner">
            <div className="audit-cta-left">
              <h2>Vaše firma na mobilu <span className="audit-gold">do 10 dní</span>.</h2>
              <p>30 minut konzultace. Zdarma. Dozvíte se konkrétní cenu a co přesně bude aplikace ukazovat u vaší firmy.</p>
              <div className="audit-cta-mail">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="22 6 12 13 2 6" />
                </svg>
                obchod@syncrox.cz
              </div>
              <div className="audit-cta-note">Odpovídáme do 24 hodin.</div>

              {/* Mini quote near form */}
              <div style={{
                marginTop: 28,
                padding: '20px 22px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 16,
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 14,
                lineHeight: 1.55,
                maxWidth: 440,
              }}>
                <div style={{ color: '#FDBA74', letterSpacing: '-1px', fontSize: 14, marginBottom: 6 }}>★★★★★</div>
                „<strong style={{ color: '#FFFFFF' }}>Nasazení do 8 dní. Aplikace funguje. Úpravy děláme průběžně.</strong> Tohle není prezentace — to je realita."
                <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>— Ondřej Krejčí, majitel e-shopu s výrobou</div>
              </div>
            </div>
            <ReportForm />
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="audit-footer">
        <div className="audit-container">
          <div className="audit-footer-inner">
            <div>© 2026 SyncroX — Platforma pro digitalizaci firem</div>
            <div>
              <a href="/">Hlavní stránka</a>
              <a href="/digitalizacni-audit">Digitalizační audit</a>
              <a href="mailto:obchod@syncrox.cz">Kontakt</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ========== EXIT INTENT POPUP ========== */}
      <ExitIntent />
    </div>
  );
}
